import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  createDeliveryBoy,
  getDeliveryBoys,
  updateDeliveryBoy,
  deleteDeliveryBoy,
} from "../../api/adminApi";

import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
  DialogActions,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Chip,
  Box,
  CircularProgress,
  TableContainer,
  Stack,
  Card,
  CardContent,
  Divider,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// Modern input styling matching the application's aesthetic
const modernInputStyle = {
  '& .MuiInputLabel-root': {
    color: '#A59D96',
    fontWeight: '600',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#D97706'
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: '#FFFFFF',
    '& fieldset': {
      borderColor: '#ECE5DA',
    },
    '&:hover fieldset': {
      borderColor: '#F29D17',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#F8C332',
      borderWidth: '2px'
    }
  },
  '& .MuiOutlinedInput-input': {
    fontWeight: '700',
    color: '#2F2A25',
  }
};

export default function DeliveryBoys() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [boys, setBoys] = useState([]);
  const [loading, setLoading] = useState(true);

  // States for Delete Confirmation Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [boyToDelete, setBoyToDelete] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
  });

  // =========================
  // FETCH DELIVERY BOYS
  // =========================
  const fetchData = async () => {
    try {
      const res = await getDeliveryBoys();
      setBoys(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // AUTO REFRESH
  // =========================
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, []);

  // =========================
  // CREATE / UPDATE
  // =========================
  const handleSubmit = async () => {
    try {
      if (editMode) {
        await updateDeliveryBoy(selectedId, form);
        setSnackbar({ open: true, message: "Updated Successfully!", severity: "success" });
      } else {
        await createDeliveryBoy(form);
        setSnackbar({ open: true, message: "Created Successfully!", severity: "success" });
      }

      setOpen(false);
      setEditMode(false);
      setForm({
        name: "", email: "", phone: "", password: "",
      });

      fetchData();
    } catch (err) {
      const errorMessage = err?.response?.data?.detail || err?.response?.data?.error || "Server Error: Check Console";
      setSnackbar({ open: true, message: errorMessage, severity: "error" });
      console.error("Update Error:", err);
    }
  };

  // =========================
  // EDIT
  // =========================
  const handleEdit = (boy) => {
    setEditMode(true);
    setSelectedId(boy.id);
    setForm({
      name: boy.name,
      email: boy.email,
      phone: boy.phone,
      password: "",
    });
    setOpen(true);
  };

  // =========================
  // DELETE CONFIRMATION & ACTION
  // =========================
  const handleDeleteClick = (id) => {
    setBoyToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteDeliveryBoy(boyToDelete);
      fetchData();
      setSnackbar({ open: true, message: "Delivery Boy deleted.", severity: "success" });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.error || "Error deleting",
        severity: "error"
      });
    } finally {
      setDeleteDialogOpen(false);
      setBoyToDelete(null);
    }
  };

  // =========================
  // STATUS COLOR
  // =========================
  const getStatusColor = (status) => {
    switch (status) {
      case "IDLE":
        return "success";
      case "BUSY":
        return "warning";
      case "ON_DELIVERY":
        return "info";
      case "OFFLINE":
      default:
        return "default";
    }
  };

  // =========================
  // LOADING
  // =========================
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="#FFFDF8">
        <CircularProgress size={60} thickness={4} sx={{ color: '#F8C332' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 1200, mx: "auto", minHeight: "100vh", bgcolor: "#FFFDF8", marginBottom: 15 }}>

      {/* ========================= */}
      {/* HEADER */}
      {/* ========================= */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        gap={2}
        mb={4}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              bgcolor: '#FFFFFF',
              border: '1px solid #ECE5DA',
              color: '#3A2C23',
              '&:hover': { bgcolor: '#F8C332', borderColor: '#F8C332' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight="900" sx={{ color: "#3A2C23", letterSpacing: "-0.5px" }}>
            Delivery Boys
          </Typography>
        </Box>

        <Button
          variant="contained"
          size="large"
          disableElevation
          onClick={() => {
            setEditMode(false);
            setForm({
              name: "",
              email: "",
              phone: "",
              password: "",
            });
            setOpen(true);
          }}
          sx={{
            borderRadius: '16px',
            textTransform: "none",
            fontWeight: 900,
            bgcolor: '#F8C332',
            color: '#3A2C23',
            boxShadow: '0 4px 12px rgba(248, 195, 50, 0.3)',
            '&:hover': { bgcolor: '#F5B71D' },
            py: { xs: 1.5, sm: 1.2 },
            px: 3,
            width: { xs: "100%", sm: "auto" }
          }}
        >
          + Add Delivery Boy
        </Button>
      </Box>

      {/* ========================= */}
      {/* TABLE / MOBILE CARD */}
      {/* ========================= */}
      {/* Removed Outer Paper Wrapper */}

      {isMobile ? (
        <Stack spacing={2}>
          {boys.map((b) => (
            <Card
              key={b.id}
              elevation={0}
              sx={{
                borderRadius: '20px',
                border: "none",
                bgcolor: '#FFFFFF',
                boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)',
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6" fontWeight="900" sx={{ color: '#E85D3F' }}>
                    {b.name}
                  </Typography>
                  <Box display="flex" gap={1}>
                    <Chip
                      label={b.active ? "Online" : "Offline"}
                      color={b.active ? "success" : "default"}
                      size="small"
                      variant={b.active ? "filled" : "outlined"}
                      sx={{ fontWeight: 800, borderRadius: '8px' }}
                    />
                    <Chip
                      label={b.status}
                      color={getStatusColor(b.status)}
                      size="small"
                      sx={{ fontWeight: 800, borderRadius: '8px' }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 1.5, borderStyle: 'dashed', borderColor: '#ECE5DA' }} />

                <Stack spacing={1.5}>
                  <Typography variant="body2" color="#6E655E" fontWeight="600">
                    <strong style={{ color: '#3A2C23' }}>Email:</strong> {b.email}
                  </Typography>

                  <Typography variant="body2" color="#6E655E" fontWeight="600">
                    <strong style={{ color: '#3A2C23' }}>Phone:</strong> {b.phone}
                  </Typography>

                  {/* WORK */}
                  <Box>
                    {b.current_order_id && (
                      <Chip
                        label={`Order #${b.current_order_id}`}
                        color="warning"
                        size="small"
                        sx={{ mr: 1, mb: 1, fontWeight: 700, borderRadius: '8px' }}
                      />
                    )}

                    {b.current_batch_id && (
                      <Chip
                        label={`Batch #${b.current_batch_id}`}
                        color="info"
                        size="small"
                        sx={{ mb: 1, fontWeight: 700, borderRadius: '8px' }}
                      />
                    )}

                    {!b.current_order_id && !b.current_batch_id && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight="700"
                      >
                        No Active Work
                      </Typography>
                    )}
                  </Box>

                  {/* ACTIONS */}
                  <Box mt={1} pt={2} borderTop="1px dashed #ECE5DA" display="flex" justifyContent="flex-end" gap={1}>
                    <IconButton sx={{ color: '#F29D17', bgcolor: '#FFF6DE', '&:hover': { bgcolor: '#F8C332', color: '#3A2C23' } }} onClick={() => handleEdit(b)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton sx={{ color: '#E85D3F', bgcolor: '#FFF0EE', '&:hover': { bgcolor: '#E85D3F', color: '#FFF' } }} onClick={() => handleDeleteClick(b.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ))}
          {boys.length === 0 && (
            <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: '20px', border: "1px dashed #ECE5DA", bgcolor: '#FFFFFF' }}>
              <Typography color="text.secondary" fontWeight="700">No delivery boys found.</Typography>
            </Paper>
          )}
        </Stack>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: '20px',
            border: "none",
            bgcolor: '#FFFFFF',
            boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              transform: 'translateY(-3px)',
              boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)',
            }
          }}
        >
          <Table sx={{ minWidth: 800 }}>
            <TableHead sx={{ bgcolor: "#FFFDF8" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Active</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }}>Current Work</TableCell>
                <TableCell sx={{ fontWeight: 800, color: '#A59D96' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {boys.map((b) => (
                <TableRow key={b.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="900" sx={{ color: '#E85D3F' }}>
                      {b.name}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#3A2C23' }}>{b.email}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#6E655E' }}>{b.phone}</TableCell>

                  {/* ACTIVE */}
                  <TableCell>
                    <Chip
                      label={b.active ? "Online" : "Offline"}
                      color={b.active ? "success" : "default"}
                      size="small"
                      variant={b.active ? "filled" : "outlined"}
                      sx={{ fontWeight: 800, borderRadius: '6px' }}
                    />
                  </TableCell>

                  {/* STATUS */}
                  <TableCell>
                    <Chip
                      label={b.status}
                      color={getStatusColor(b.status)}
                      size="small"
                      sx={{ fontWeight: 800, borderRadius: '6px' }}
                    />
                  </TableCell>

                  {/* CURRENT WORK */}
                  <TableCell>
                    {b.current_order_id && (
                      <Chip
                        label={`Order #${b.current_order_id}`}
                        color="warning"
                        size="small"
                        sx={{ mr: 1, fontWeight: 700, borderRadius: '6px' }}
                      />
                    )}

                    {b.current_batch_id && (
                      <Chip
                        label={`Batch #${b.current_batch_id}`}
                        color="info"
                        size="small"
                        sx={{ fontWeight: 700, borderRadius: '6px' }}
                      />
                    )}

                    {!b.current_order_id &&
                      !b.current_batch_id &&
                      <Typography variant="body2" color="text.secondary" fontWeight="700">No Active Work</Typography>}
                  </TableCell>

                  {/* ACTIONS */}
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton size="small" sx={{ color: '#F29D17', bgcolor: '#FFF6DE', '&:hover': { bgcolor: '#F8C332', color: '#3A2C23' }, mr: 1 }} onClick={() => handleEdit(b)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" sx={{ color: '#E85D3F', bgcolor: '#FFF0EE', '&:hover': { bgcolor: '#E85D3F', color: '#FFF' } }} onClick={() => handleDeleteClick(b.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {boys.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary', fontWeight: 700 }}>
                    No delivery boys found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ========================= */}
      {/* FORM DIALOG (Add/Edit) */}
      {/* ========================= */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={fullScreenDialog}
        PaperProps={{
          sx: {
            borderRadius: fullScreenDialog ? 0 : '24px',
            bgcolor: '#FFFDF8',
            boxShadow: '0 24px 48px rgba(0,0,0,0.2)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, pb: 2, pt: 3, px: { xs: 2, sm: 4 }, color: '#3A2C23', borderBottom: '1px dashed #ECE5DA' }}>
          {editMode ? "Edit Delivery Boy" : "Add Delivery Boy"}
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, sm: 4 }, pt: "24px !important", pb: { xs: '15vh', sm: 4 } }}>
          <Stack spacing={2.5}>
            <TextField
              sx={modernInputStyle}
              label="Name"
              fullWidth
              value={form.name}
              onChange={(e) =>
                setForm({ ...form, name: e.target.value })
              }
            />

            <TextField
              sx={modernInputStyle}
              label="Email"
              fullWidth
              value={form.email}
              onChange={(e) =>
                setForm({ ...form, email: e.target.value })
              }
            />

            <TextField
              sx={modernInputStyle}
              label="Phone"
              fullWidth
              value={form.phone}
              onChange={(e) =>
                setForm({ ...form, phone: e.target.value })
              }
            />

            <TextField
              sx={modernInputStyle}
              label="Password"
              type="password"
              fullWidth
              value={form.password}
              onChange={(e) =>
                setForm({ ...form, password: e.target.value })
              }
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: { xs: 2, sm: 3 }, px: { sm: 4 }, borderTop: "1px solid #ECE5DA", bgcolor: '#FFFFFF', borderRadius: '0 0 24px 24px' }}>
          <Button
            onClick={() => setOpen(false)}
            color="inherit"
            sx={{ textTransform: "none", fontWeight: 800, color: '#6E655E', borderRadius: '12px', px: 3 }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            disableElevation
            onClick={handleSubmit}
            sx={{
              textTransform: "none",
              fontWeight: 900,
              borderRadius: '12px',
              bgcolor: '#F8C332',
              color: '#3A2C23',
              px: 4,
              py: 1.2,
              boxShadow: '0 4px 12px rgba(248, 195, 50, 0.3)',
              '&:hover': { bgcolor: '#F5B71D' }
            }}
          >
            {editMode ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================= */}
      {/* DELETE CONFIRMATION DIALOG */}
      {/* ========================= */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '20px', bgcolor: '#FFFDF8' } }}
      >
        <DialogTitle sx={{ fontWeight: 900, color: '#3A2C23' }}>Delete Delivery Boy?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#6E655E", fontWeight: 600 }}>
            Are you sure you want to delete this delivery boy?
          </DialogContentText>
          <DialogContentText sx={{ color: "#DC2626", mt: 2, fontSize: "0.875rem", fontWeight: 700 }}>
            This action is permanent and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontWeight: 800, color: '#6E655E', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={confirmDelete}
            sx={{
              fontWeight: 800,
              borderRadius: '10px',
              textTransform: 'none',
              bgcolor: '#DC2626',
              color: '#FFFFFF',
              '&:hover': { bgcolor: '#B91C1C' }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================= */}
      {/* BEAUTIFUL SNACKBAR ALERT */}
      {/* ========================= */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '12px', fontWeight: 'bold' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
}